/**
 * AuthPage Component — Premium v2
 * 
 * Frosted glass design with Outfit headings, Manrope body.
 * Compass-born palette: accent purple, emerald success, void background.
 */

import React, { useState } from 'react';
import { brand, getPrimaryButtonStyle } from '../../styles/brand';
import { SmartPlAIteWordmark } from '../brand/BrandLogo';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

const inputStyle = {
  width: '100%',
  padding: '14px 16px',
  borderRadius: radius.md,
  border: `1px solid rgba(255,255,255,0.08)`,
  background: 'rgba(255,255,255,0.04)',
  color: colors.text,
  fontSize: '14px',
  fontFamily: typography.fontFamily,
  fontWeight: '400',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  boxSizing: 'border-box',
  letterSpacing: '0.01em',
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontSize: '11px',
  color: colors.textSecondary,
  fontFamily: typography.headingFont,
  fontWeight: '500',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const AuthPage = ({
  authView,
  setAuthView,
  authForm,
  setAuthForm,
  authError,
  setAuthError,
  handleLogin,
  handleSignup,
  handleGoogleSignIn,
  pendingPromoCode,
  setPendingPromoCode
}) => {
  const [showPromoInput, setShowPromoInput] = useState(false);

  return (
    <div className="app" style={{ background: colors.bg0 }}>
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        background: gradients.background,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative compass ring glow */}
        <div style={{
          position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,92,252,0.06) 0%, transparent 60%)',
          pointerEvents: 'none'
        }} />

        {/* Logo & Tagline */}
        <div style={{ textAlign: 'center', marginBottom: '48px', position: 'relative' }}>
          <SmartPlAIteWordmark size="lg" variant="dark" style={{ marginBottom: '12px' }} />
          <p style={{
            color: colors.textSecondary,
            fontSize: '14px',
            fontFamily: typography.fontFamily,
            fontWeight: '400',
            letterSpacing: '0.02em',
            margin: 0,
          }}>
            Your Plate Builds Your Intelligence
          </p>
        </div>

        {/* Auth Card — Frosted Glass */}
        <div style={{
          width: '100%',
          maxWidth: '380px',
          background: 'rgba(255, 255, 255, 0.04)',
          borderRadius: radius.xl,
          padding: '32px 28px',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          position: 'relative',
        }}>
          {/* Toggle Tabs */}
          <div style={{
            display: 'flex',
            gap: '4px',
            marginBottom: '28px',
            background: 'rgba(255,255,255,0.04)',
            padding: '4px',
            borderRadius: radius.md,
          }}>
            {['login', 'signup'].map(view => (
              <button
                key={view}
                onClick={() => { setAuthView(view); setAuthError(''); }}
                style={{
                  flex: 1,
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: authView === view ? gradients.primary : 'transparent',
                  color: authView === view ? '#fff' : colors.textSecondary,
                  fontFamily: typography.headingFont,
                  fontWeight: authView === view ? '600' : '400',
                  fontSize: '13px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  letterSpacing: '0.01em',
                  boxShadow: authView === view ? shadows.glow : 'none',
                }}
              >
                {view === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Error Message */}
          {authError && (
            <div data-testid="auth-error" style={{
              padding: '12px 14px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: radius.md,
              marginBottom: '20px',
              color: colors.error,
              fontSize: '12px',
              fontFamily: typography.fontFamily,
              fontWeight: '500'
            }}>
              {authError}
            </div>
          )}

          {/* Auth Form */}
          <form onSubmit={authView === 'login' ? handleLogin : handleSignup}>
            {authView === 'signup' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Full Name</label>
                <input
                  type="text"
                  value={authForm.name}
                  onChange={(e) => setAuthForm({ ...authForm, name: e.target.value })}
                  placeholder="Jane Doe"
                  required
                  style={inputStyle}
                  onFocus={(e) => { e.target.style.borderColor = 'rgba(124,92,252,0.4)'; e.target.style.boxShadow = '0 0 0 3px rgba(124,92,252,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
                />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Email Address</label>
              <input
                type="email"
                data-testid="auth-email-input"
                value={authForm.email}
                onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                placeholder="you@example.com"
                required
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(124,92,252,0.4)'; e.target.style.boxShadow = '0 0 0 3px rgba(124,92,252,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div style={{ marginBottom: '28px' }}>
              <label style={labelStyle}>Password</label>
              <input
                type="password"
                data-testid="auth-password-input"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                placeholder="Enter password"
                required
                minLength={6}
                style={inputStyle}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(124,92,252,0.4)'; e.target.style.boxShadow = '0 0 0 3px rgba(124,92,252,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.08)'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <button
              type="submit"
              data-testid="auth-submit-btn"
              style={{
                ...getPrimaryButtonStyle({ fullWidth: true }),
                padding: '14px 24px',
                fontSize: '14px',
                fontFamily: typography.headingFont,
                fontWeight: '600',
                letterSpacing: '0.02em',
              }}
            >
              {authView === 'login' ? 'Sign In' : 'Create Account'}
            </button>
          </form>

          {/* Divider */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            margin: '24px 0',
            gap: '12px'
          }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
            <span style={{
              color: colors.mutedText,
              fontSize: '11px',
              fontFamily: typography.fontFamily,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              fontWeight: '500'
            }}>
              or
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.06)' }} />
          </div>

          {/* Google Sign In */}
          <button
            data-testid="google-signin-btn"
            onClick={() => {
              if (pendingPromoCode && pendingPromoCode.trim()) {
                localStorage.setItem('pending_promo_code', pendingPromoCode.trim());
              }
              handleGoogleSignIn();
            }}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: radius.md,
              border: '1px solid rgba(255,255,255,0.10)',
              background: 'rgba(255,255,255,0.06)',
              color: colors.text,
              fontSize: '13px',
              fontWeight: '500',
              fontFamily: typography.fontFamily,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s ease',
              backdropFilter: 'blur(8px)',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
        </div>

        {/* Trial Banner + Promo Code */}
        <div style={{
          marginTop: '32px',
          padding: '20px 24px',
          background: 'rgba(124, 92, 252, 0.06)',
          border: '1px solid rgba(124, 92, 252, 0.15)',
          borderRadius: radius.xl,
          textAlign: 'center',
          maxWidth: '380px',
          width: '100%',
          backdropFilter: 'blur(12px)',
        }}>
          <p style={{
            margin: 0,
            fontSize: '13px',
            color: colors.accent,
            fontWeight: '600',
            fontFamily: typography.headingFont,
            letterSpacing: '0.01em'
          }}>
            Free Premium Trial After Signup
          </p>
          <p style={{
            margin: '6px 0 0 0',
            fontSize: '11px',
            color: colors.textSecondary,
            fontFamily: typography.fontFamily,
            fontWeight: '400'
          }}>
            7 days free &middot; or 30 days with a promo code
          </p>

          {!showPromoInput ? (
            <button
              data-testid="auth-show-promo-btn"
              onClick={() => setShowPromoInput(true)}
              style={{
                marginTop: '12px',
                padding: '8px 20px',
                background: 'transparent',
                border: '1px dashed rgba(124, 92, 252, 0.3)',
                borderRadius: radius.pill,
                color: colors.accent,
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: typography.fontFamily,
                transition: 'all 0.2s ease',
                letterSpacing: '0.02em'
              }}
            >
              Have a promo code?
            </button>
          ) : (
            <div data-testid="auth-promo-section" style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                <input
                  data-testid="auth-promo-input"
                  type="text"
                  value={pendingPromoCode || ''}
                  onChange={(e) => setPendingPromoCode(e.target.value.toUpperCase())}
                  placeholder="ENTER CODE"
                  maxLength={10}
                  style={{
                    flex: 1,
                    maxWidth: '180px',
                    padding: '10px 14px',
                    borderRadius: radius.md,
                    border: '1px solid rgba(124, 92, 252, 0.25)',
                    background: 'rgba(255,255,255,0.04)',
                    color: colors.text,
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: typography.monoFont,
                    letterSpacing: '3px',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    outline: 'none',
                  }}
                />
              </div>
              {pendingPromoCode && (
                <p style={{
                  margin: '8px 0 0', fontSize: '11px', fontWeight: '600',
                  color: colors.free, fontFamily: typography.fontFamily
                }}>
                  Code &quot;{pendingPromoCode}&quot; will be applied on signup
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
